import { Model, Factory, hasMany } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randFullName, randNumber } from '@ngneat/falso'

import type { MirageServer, MockServer } from './server.js'
import { pageable, sortable, generateFinanceAmount } from './utils.js'

type DevPlaygroundPatientBilling = {
  invoice_count: number
  full_name: string
  total_paid: string
  total_unpaid: string
}

export const model = Model.extend({
  invoices: hasMany('devPlaygroundPatientBillingInvoice'), // Correctly name the association
}) as ModelDefinition<DevPlaygroundPatientBilling>

export const factory = Factory.extend<DevPlaygroundPatientBilling>({
  invoice_count: 0,
  full_name: () => randFullName(),
  total_paid: () => generateFinanceAmount({ min: 500, max: 2500, dec: 2 }),
  total_unpaid: () => generateFinanceAmount({ min: 0, max: 500, dec: 2 }),
})

export function routes(server: MockServer) {
  server.resource('devPlaygroundPatientBilling')

  server.get(
    '/dev-playground/patient-billings',
    function (this: MirageServer, schema, request) {
      // 🚨 Fake endpoint – only for development purposes.
      // This endpoint is used exclusively for testing and demonstrating advanced filtering for Tables in the Dev Playground.
      // It leverages the Patients Model to simulate real data, allowing developers to play around with filtering the table's content,
      // by search, status, gender, species, along with sorting.
      const patientBilling = schema.all('devPlaygroundPatientBilling')
      const sorted = sortable(patientBilling, request)

      // then paginate
      const { data, meta } = pageable(sorted, request)

      // and finally construct the response
      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )
}

export function seeds(server: MockServer) {
  server.createList('devPlaygroundPatientBilling', 35).forEach((billing) => {
    // Generate a random number of invoices for each billing
    const invoicesCount = randNumber({ min: 1, max: 12 })

    // Create a list of invoices and associate them with the current billing
    const invoices = server.createList(
      'devPlaygroundPatientBillingInvoice',
      invoicesCount,
      {
        billing, // Associate each invoice with the current billing
      },
    )

    // Update the invoicesCount field in the billing model
    billing.update({ invoice_count: invoices.length })
  })
}
